import React, { useEffect, useMemo, useState } from "react";

import {
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    CircularProgress,
    Chip,
    Stack,
    Card
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";
import GroupsIcon from "@mui/icons-material/Groups";

import {
    collection,
    getDocs
} from "firebase/firestore";

import { db } from "../../firebase";

function Allslaryslip() {

    const [loading, setLoading] = useState(true);

    const [staffList, setStaffList] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [advances, setAdvances] = useState([]);

    const [salaryReport, setSalaryReport] = useState([]);

    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1
    );

    const [selectedYear, setSelectedYear] = useState(
        new Date().getFullYear()
    );

    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];

    const years = [2024, 2025, 2026, 2027];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {

        try {

            setLoading(true);

            const [staffSnap, attendanceSnap, advanceSnap] = await Promise.all([
                getDocs(collection(db, "staff")),
                getDocs(collection(db, "staffattendance")),
                getDocs(collection(db, "staffadvances"))
            ]);

            setStaffList(
                staffSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            );

            setAttendance(
                attendanceSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            );

            setAdvances(
                advanceSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            );

        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const generateReport = () => {

        let report = [];

        staffList.forEach((staff) => {

            let present = 0;
            let absent = 0;
            let halfday = 0;

            attendance.forEach((att) => {

                if (att.staffId !== staff.id) return;

                if (!att.date) return;

                const attDate = new Date(att.date);

                const month = attDate.getMonth() + 1;
                const year = attDate.getFullYear();

                if (
                    Number(month) === Number(selectedMonth) &&
                    Number(year) === Number(selectedYear)
                ) {

                    if (att.status === "Present") {
                        present++;
                    }

                    else if (att.status === "Absent") {
                        absent++;
                    }

                    else if (att.status === "Half Day") {
                        halfday++;
                    }
                }
            });

            const monthlySalary = Number(staff.payment || 0);

            const perDaySalary = monthlySalary / 30;

            const earnedSalary =
                (present * perDaySalary) +
                (halfday * perDaySalary * 0.5);

            const pendingAdvance = advances
                .filter(
                    (adv) =>
                        adv.staffId === staff.id &&
                        adv.status === "Pending"
                )
                .reduce(
                    (sum, adv) =>
                        sum + Number(adv.payment || 0),
                    0
                );

            const netSalary = earnedSalary - pendingAdvance;

            report.push({
                staffId: staff.id,
                name: staff.name || "-",
                post: staff.post || "-",
                mobile: staff.mobile || "-",
                monthlySalary,
                present,
                absent,
                halfday,
                earnedSalary,
                pendingAdvance,
                netSalary
            });
        });

        setSalaryReport(report);
    };

    useEffect(() => {
        if (staffList.length > 0) {
            generateReport();
        }
    }, [staffList, selectedMonth, selectedYear]);

    const totalNetSalary = useMemo(() => {

        return salaryReport.reduce(
            (sum, row) =>
                sum + Number(row.netSalary || 0),
            0
        );

    }, [salaryReport]);

    const totalAdvance = useMemo(() => {

        return salaryReport.reduce(
            (sum, row) =>
                sum + Number(row.pendingAdvance || 0),
            0
        );

    }, [salaryReport]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="80vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                bgcolor: "#f4f6f8",
                minHeight: "100vh"
            }}
        >

            {/* HEADER */}
            <Paper
                sx={{
                    p: 2,
                    borderRadius: 3,
                    mb: 2
                }}
            >

                <Stack
                    direction={{
                        xs: "column",
                        md: "row"
                    }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{
                        xs: "stretch",
                        md: "center"
                    }}
                >

                    <Box>
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            <GroupsIcon color="primary" />
                            All Staff Salary Slip Report
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Monthly Staff Salary Summary
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={2}
                    >

                        <FormControl
                            size="small"
                            sx={{ minWidth: 160 }}
                        >
                            <InputLabel>
                                Month
                            </InputLabel>

                            <Select
                                value={selectedMonth}
                                label="Month"
                                onChange={(e) =>
                                    setSelectedMonth(e.target.value)
                                }
                            >

                                {months.map((month, index) => (
                                    <MenuItem
                                        key={index}
                                        value={index + 1}
                                    >
                                        {month}
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>

                        <FormControl
                            size="small"
                            sx={{ minWidth: 120 }}
                        >
                            <InputLabel>
                                Year
                            </InputLabel>

                            <Select
                                value={selectedYear}
                                label="Year"
                                onChange={(e) =>
                                    setSelectedYear(e.target.value)
                                }
                            >

                                {years.map((year) => (
                                    <MenuItem
                                        key={year}
                                        value={year}
                                    >
                                        {year}
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                        >
                            Print
                        </Button>

                    </Stack>

                </Stack>

            </Paper>

            {/* SUMMARY */}
            <Grid container spacing={2} mb={2}>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            borderRadius: 3
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Total Staff
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="primary"
                        >
                            {salaryReport.length}
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            borderRadius: 3
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Total Advance
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="error.main"
                        >
                            ₹{totalAdvance.toFixed(0)}
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            borderRadius: 3
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Total Net Salary
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="success.main"
                        >
                            ₹{totalNetSalary.toFixed(0)}
                        </Typography>
                    </Card>
                </Grid>

            </Grid>

            {/* TABLE */}
            <Paper
                className="printable-area"
                sx={{
                    borderRadius: 3,
                    overflow: "hidden"
                }}
            >
                <Box textAlign="center" mb={3} mt={2}>
                    <Typography variant="h5" fontWeight="bold">Vishwajeet Khadi Kendra</Typography>
                    <Typography variant="body2">Jahagirdarwadi, Dist. Dharashiv, Maharashtra</Typography>
                    <Typography variant="h6" color="text.secondary" display="block">
                        Salary Report - {months[selectedMonth - 1]} {selectedYear}
                    </Typography>
                </Box>


                <TableContainer>

                    <Table size="small">

                        <TableHead>

                            <TableRow>

                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Sr No
                                </TableCell>

                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Staff Name
                                </TableCell>

                                {/* <TableCell sx={{ fontWeight: "bold" }}>
                                    Post
                                </TableCell>

                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Mobile
                                </TableCell> */}

                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    Present
                                </TableCell>

                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    Half
                                </TableCell>

                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    Absent
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    Gross
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    Advance
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    Net Salary
                                </TableCell>

                            </TableRow>

                        </TableHead>

                        <TableBody>

                            {salaryReport.length === 0 ? (

                                <TableRow>
                                    <TableCell
                                        colSpan={10}
                                        align="center"
                                    >
                                        No Data Found
                                    </TableCell>
                                </TableRow>

                            ) : (

                                <>
                                    {salaryReport.map((row, index) => (

                                        <TableRow
                                            key={row.staffId}
                                            hover
                                        >

                                            <TableCell>
                                                {index + 1}
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="600"
                                                >
                                                    {row.name}
                                                </Typography>
                                            </TableCell>
                                            {/* 
                                            <TableCell>
                                                <Chip
                                                    label={row.post}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            <TableCell>
                                                {row.mobile}
                                            </TableCell> */}

                                            <TableCell align="center">
                                                <Typography
                                                    color="success.main"
                                                    fontWeight="bold"
                                                >
                                                    {row.present}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography
                                                    color="warning.main"
                                                    fontWeight="bold"
                                                >
                                                    {row.halfday}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography
                                                    color="error.main"
                                                    fontWeight="bold"
                                                >
                                                    {row.absent}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                ₹{row.monthlySalary.toFixed(0)}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    color: "error.main",
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                ₹{row.pendingAdvance.toFixed(0)}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    color: "success.main",
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                ₹{row.netSalary.toFixed(0)}
                                            </TableCell>

                                        </TableRow>

                                    ))}

                                    <TableRow
                                        sx={{
                                            bgcolor: "#f8fafc"
                                        }}
                                    >

                                        <TableCell
                                            colSpan={6}
                                            align="right"
                                            sx={{
                                                fontWeight: "bold"
                                            }}
                                        >
                                            Grand Total
                                        </TableCell>

                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "error.main"
                                            }}
                                        >
                                            ₹{totalAdvance.toFixed(0)}
                                        </TableCell>

                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "success.main"
                                            }}
                                        >
                                            ₹{totalNetSalary.toFixed(0)}
                                        </TableCell>

                                    </TableRow>
                                </>
                            )}

                        </TableBody>

                    </Table>

                </TableContainer>

            </Paper>

            <style>{`
                @media print {

                    body * {
                        visibility: hidden;
                    }

                    .printable-area,
                    .printable-area * {
                        visibility: visible;
                    }

                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }

                    @page {
                        size: landscape;
                        margin: 10mm;
                    }
                }
            `}</style>

        </Box>
    );
}

export default Allslaryslip;